import React, { createContext, FC, useEffect, useState, useContext } from "react";
import { BenchmarkingQHPService, BenchmarkingService } from "@app/overviews/network-benchmarking-dashboard";
import { BenchmarkingSpecialtyGroup } from "@app/overviews/network-benchmarking-dashboard/filters/network-comparison-filters/shared";
import {
  BenchmarkingLineOfBusinessEnum,
  BenchmarkingNetworkType,
  BenchmarkingPrimaryFiltersPayloadType,
  BenchmarkingState,
} from "@app/overviews/network-benchmarking-dashboard/filters/shared";
import { BenchmarkingServiceType } from "@app/overviews/network-benchmarking-dashboard/filters/shared/utility";
import { UserContext } from "@app/user";

export interface BenchmarkingFiltersContextValues {
  displayedNetworkFilters: BenchmarkingNetworkType[];
  displayedPrimaryFilters: BenchmarkingPrimaryFiltersPayloadType;
  getService: () => BenchmarkingServiceType;
  linesOfBusiness: BenchmarkingLineOfBusinessEnum[];
  linesOfBusinessLoading: boolean;
  selectedLineOfBusiness: number;
  selectedNetworks: BenchmarkingNetworkType[];
  setDisplayedNetworkFilters: (networkFilters: BenchmarkingNetworkType[]) => void;
  setDisplayedPrimaryFilters: (primaryFilters: BenchmarkingPrimaryFiltersPayloadType) => void;
  setSelectedLineOfBusiness: (selectedLineOfBusiness: number) => void;
  setSelectedNetworks: (selectedHContracts: BenchmarkingNetworkType[]) => void;
  setSpecialties: (specialties: BenchmarkingSpecialtyGroup[]) => void;
  setStates: (states: BenchmarkingState[]) => void;
  specialties: BenchmarkingSpecialtyGroup[];
  states: BenchmarkingState[];
}

const initialValues: BenchmarkingFiltersContextValues = {
  displayedNetworkFilters: [],
  displayedPrimaryFilters: null,
  getService: () => null,
  linesOfBusiness: [],
  linesOfBusinessLoading: false,
  selectedLineOfBusiness: null,
  selectedNetworks: [],
  setDisplayedNetworkFilters: () => null,
  setDisplayedPrimaryFilters: () => null,
  setSelectedLineOfBusiness: () => null,
  setSelectedNetworks: () => null,
  setSpecialties: () => null,
  setStates: () => null,
  specialties: [],
  states: [],
}

export const BenchmarkingFiltersContext = createContext(initialValues);

const BenchmarkingFiltersContextProvider: FC<{ children: React.ReactNode }> = (props: { children: React.ReactNode }) => {
  const [displayedNetworkFilters, setDisplayedNetworkFilters] = useState<BenchmarkingNetworkType[]>(initialValues.displayedNetworkFilters);
  const [displayedPrimaryFilters, setDisplayedPrimaryFilters] = useState<BenchmarkingPrimaryFiltersPayloadType>(initialValues.displayedPrimaryFilters);
  const [linesOfBusiness, setLinesOfBusiness] = useState<BenchmarkingLineOfBusinessEnum[]>(initialValues.linesOfBusiness);
  const [linesOfBusinessLoading, setLinesOfBusinessLoading] = useState<boolean>(initialValues.linesOfBusinessLoading);
  const [selectedNetworks, setSelectedNetworks] = useState<BenchmarkingNetworkType[]>(initialValues.selectedNetworks);
  const [selectedLineOfBusiness, setSelectedLineOfBusiness] = useState<number>(initialValues.selectedLineOfBusiness);
  const [specialties, setSpecialties] = useState<BenchmarkingSpecialtyGroup[]>(initialValues.specialties);
  const [states, setStates] = useState<BenchmarkingState[]>(initialValues.states);

  const userContext = useContext(UserContext);

  const benchmarkingQHPService = new BenchmarkingQHPService();
  const benchmarkingService = new BenchmarkingService();

  useEffect(() => {
    setLinesOfBusinessLoading(true);

    benchmarkingService.getBenchmarkingLinesOfBusiness(userContext.clientId).then((data) => {
      if (!!data) {
        const medicare: BenchmarkingLineOfBusinessEnum = data.find(x => x === BenchmarkingLineOfBusinessEnum.Medicare);

        if (!!medicare) {
          setSelectedLineOfBusiness(medicare);
        }

        setLinesOfBusiness(data);
      }

      setLinesOfBusinessLoading(false);
    });
  }, []);

  const getService = (): BenchmarkingServiceType => {
    switch (selectedLineOfBusiness) {
      case BenchmarkingLineOfBusinessEnum.Marketplace:
        return benchmarkingQHPService;
      case BenchmarkingLineOfBusinessEnum.Medicare:
        return benchmarkingService;
      default:
        return;
    }
  }

  return (
    <BenchmarkingFiltersContext.Provider
      value={{
        displayedNetworkFilters,
        displayedPrimaryFilters,
        getService,
        linesOfBusiness,
        linesOfBusinessLoading,
        selectedLineOfBusiness,
        selectedNetworks,
        setDisplayedNetworkFilters,
        setDisplayedPrimaryFilters,
        setSelectedLineOfBusiness,
        setSelectedNetworks,
        setSpecialties,
        setStates,
        specialties,
        states,
      }}>
      {props.children}
    </BenchmarkingFiltersContext.Provider>
  )
}

export default BenchmarkingFiltersContextProvider;